import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useSettings } from "@/components/settings-provider";
import { CrmShell } from "./crm-shell";
import { CrmFieldInput } from "./crm-field-input";
import { CrmTimeline } from "./crm-timeline";
import { CrmAudit } from "./crm-audit";
import { CrmFiles } from "./crm-files";
import { CrmFormDesigner } from "./crm-form-designer";
import { CrmBpf } from "./crm-bpf";
import { LEAD_ADDRESS_KEYS, LeadAddressPanel, useCepAutofill } from "./crm-address";
import { FieldShell, Initials, NativeSelect, ReadOnlyBox, useConfirm } from "./crm-ui";
import { ContactAvatar } from "./crm-photo";
import { useCrmText } from "@/lib/crm/i18n";
import { entityLabel, fieldDef, LEAD_MIRRORED, optionLabel, sectionLabel, stateLabel, type CrmEntity, type LeadStage } from "@/lib/crm/fields";
import {
  crmFetch, crmKeys, invalidateRecord, plural, setRecordCache, useCrmForm, useCrmRecord,
  type CrmAcceptResult, type CrmContext, type CrmError, type CrmLayoutSection, type CrmRecord,
} from "@/lib/crm/api";
import {
  apiErrorCode, changedValues, errorKey, isDirty, newRecordDraft, toDraft, validateDraft, type Draft,
} from "@/lib/crm/form-values";
import { formatDateTime, formatTime, recordTitle } from "@/lib/crm/view-engine";
import { formatPhoneDisplay, onlyDigits, phoneToE164 } from "@/lib/masks";
import { queryClient } from "@/lib/queryClient";
import {
  ArrowLeft, Ban, Loader2, Mail, MessageCircle, Phone, Plus, Power, PowerOff, RefreshCw,
  RotateCcw, Save, SaveAll, Send, Settings2, Star, Trash2, TriangleAlert,
} from "lucide-react";

/** Record page (form) of a contact or lead: /contacts/:id, /leads/:id, …/new. */
export function CrmRecordPage({ entity, id }: { entity: CrmEntity; id: string }) {
  const route = entity === "lead" ? "/leads" : "/contacts";
  return (
    <CrmShell currentPage={route}>
      {(ctx) => <RecordBody key={id} entity={entity} id={id === "new" ? null : id} ctx={ctx} />}
    </CrmShell>
  );
}

function RecordBody({ entity, id, ctx }: { entity: CrmEntity; id: string | null; ctx: CrmContext }) {
  const { lang, tx } = useCrmText();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [confirmDialog, ask] = useConfirm();
  const { sidebarPlacement } = useSettings();
  const route = entity === "lead" ? "/leads" : "/contacts";
  const isNew = !id;

  const form = useCrmForm(entity);
  const rec = useCrmRecord(entity, id);
  const layout = form.data?.layout;

  const [initial, setInitial] = useState<Draft>(() => newRecordDraft(entity, ctx.userId));
  const [draft, setDraft] = useState<Draft>(() => newRecordDraft(entity, ctx.userId));
  const [loaded, setLoaded] = useState(isNew);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [duplicate, setDuplicate] = useState<{ id: string; name: string; field: string } | null>(null);
  const [missing, setMissing] = useState<{ stage: string; fields: string[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("summary");
  const [designerOpen, setDesignerOpen] = useState(false);

  const dirty = isDirty(entity, initial, draft);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Load (and refresh) the form from the server unless the user is in the middle of an edit.
  useEffect(() => {
    if (!rec.data) return;
    if (loaded && dirtyRef.current) return;
    const d = toDraft(entity, rec.data.values);
    setInitial(d);
    setDraft(d);
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.data]);

  // Leaving the page with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const active = draft.isActive !== false;
  const readOnly = !isNew && !active;
  const record = rec.data;

  const setValue = (key: string, value: any) => {
    setDraft((d) => {
      const next = { ...d, [key]: value };
      if (entity === "lead" && LEAD_ADDRESS_KEYS.includes(key) && d.addressValidated && d[key] !== value) next.addressValidated = false;
      return next;
    });
    const isEmail = key === "personalEmail" || key === "corporateEmail";
    setErrors((e) => {
      if (!e[key] && !(key === "assignedToUserId" || key === "assignedToName") && !(isEmail && (e.personalEmail || e.corporateEmail))) return e;
      const { [key]: _drop, ...rest } = e;
      if (isEmail) { delete rest.personalEmail; delete rest.corporateEmail; }   // "one of the two" errors clear together
      return rest;
    });
    if (isEmail) setDuplicate(null);
    if (missing) setMissing(null);
  };
  const setValues = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const onPostalBlur = useCepAutofill(entity, draft, setValues, initial.postalCode);

  const messageFor = (field: string, code: string) => tx(errorKey(apiErrorCode(entity, field, code)));

  const applyServerRecord = (r: CrmRecord) => {
    setRecordCache(r);
    const d = toDraft(entity, r.values);
    setInitial(d);
    setDraft(d);
    invalidateRecord(entity, r.id);
  };

  /** Saves the form; returns the saved record (or the current one when nothing changed), null on failure. */
  const save = async (opts: { close?: boolean; quiet?: boolean } = {}): Promise<CrmRecord | null> => {
    const clientErrors = validateDraft(entity, draft, layout);
    if (Object.keys(clientErrors).length) {
      setErrors(Object.fromEntries(Object.entries(clientErrors).map(([k, c]) => [k, messageFor(k, c)])));
      setTab("summary");
      toast({ title: tx("fixErrors"), variant: "destructive" });
      return null;
    }
    const values = changedValues(entity, isNew ? null : initial, draft);
    if (!isNew && Object.keys(values).length === 0) {
      if (opts.close) navigate(route);
      return record || null;
    }
    setSaving(true);
    try {
      const r = isNew
        ? await crmFetch<CrmRecord>("POST", `/api/crm/${plural(entity)}`, { values })
        : await crmFetch<CrmRecord>("PUT", `/api/crm/${plural(entity)}/${id}`, { values });
      applyServerRecord(r);
      setErrors({});
      setDuplicate(null);
      if (!opts.quiet) toast({ title: isNew ? tx("created") : tx("saved") });
      if (opts.close) navigate(route);
      else if (isNew) navigate(`${route}/${r.id}`, { replace: true });
      return r;
    } catch (e) {
      const err = e as CrmError;
      if (err.code === "VALIDATION") {
        const fe = err.fieldErrors();
        setErrors(Object.fromEntries(Object.entries(fe).map(([k, c]) => [k, messageFor(k, c)])));
        setTab("summary");
        toast({ title: tx("fixErrors"), variant: "destructive" });
      } else if (err.code === "DUPLICATE_EMAIL") {
        const d = { id: String(err.details?.existingId || ""), name: String(err.details?.existingName || ""), field: String(err.details?.field || "personalEmail") };
        setDuplicate(d);
        setErrors((x) => ({ ...x, [d.field]: tx("duplicateEmail", { name: d.name }) }));
        setTab("summary");
        toast({ title: tx("duplicateEmail", { name: d.name }), variant: "destructive" });
      } else {
        toast({ title: tx("error"), description: err.message, variant: "destructive" });
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const guard = async (action: () => void) => {
    if (dirty) {
      const ok = await ask({ title: tx("unsavedTitle"), text: tx("unsavedText"), confirm: tx("discard"), cancel: tx("keepEditing"), destructive: true });
      if (!ok) return;
      setDraft(initial);
    }
    action();
  };

  const sendDocument = async () => {
    if (!id) return;
    if (dirty && !(await save({ quiet: true }))) return;
    navigate(`/documents/new?${entity === "lead" ? "leadId" : "contactId"}=${id}`);
  };

  const setState = async (activeNext: boolean) => {
    if (!id) return;
    if (!activeNext && dirty && !(await save({ quiet: true }))) return;
    setBusy(true);
    try {
      const r = await crmFetch<CrmRecord>("POST", `/api/crm/${plural(entity)}/${id}/state`, { active: activeNext });
      applyServerRecord(r);
      toast({ title: entity === "lead" ? (activeNext ? tx("reopened") : tx("deactivated")) : activeNext ? tx("activated") : tx("deactivated") });
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    const ok = await ask({ title: tx("confirmDeleteTitle"), text: tx("confirmDeleteOne"), confirm: tx("delete"), cancel: tx("cancel"), destructive: true });
    if (!ok) return;
    try {
      await crmFetch("DELETE", `/api/crm/${plural(entity)}/${id}`);
      queryClient.removeQueries({ queryKey: crmKeys.record(entity, id) });
      queryClient.invalidateQueries({ queryKey: crmKeys.list(entity) });
      toast({ title: tx("deleted") });
      navigate(route);
    } catch (e) {
      const err = e as CrmError;
      toast({
        title: tx("error"),
        description: err.code === "CONTACT_HAS_LEADS" ? tx("contactHasLeads", { n: err.details?.count ?? "" }) : err.message,
        variant: "destructive",
      });
    }
  };

  const disqualify = async () => {
    if (!id) return;
    const ok = await ask({ title: tx("disqualify"), text: tx("confirmDisqualify"), confirm: tx("disqualify"), cancel: tx("cancel"), destructive: true });
    if (!ok) return;
    if (dirty && !(await save({ quiet: true }))) return;
    setBusy(true);
    try {
      const r = await crmFetch<CrmRecord>("POST", `/api/crm/leads/${id}/disqualify`);
      applyServerRecord(r);
      toast({ title: tx("disqualified") });
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const moveStage = async (stage: LeadStage) => {
    if (!id) return;
    if (dirty && !(await save({ quiet: true }))) return;
    setBusy(true);
    setMissing(null);
    try {
      const r = await crmFetch<CrmRecord>("POST", `/api/crm/leads/${id}/stage`, { stage });
      applyServerRecord(r);
      toast({ title: tx("stageMoved", { stage: optionLabel("stage", stage, lang) }) });
    } catch (e) {
      const err = e as CrmError;
      if (err.code === "STAGE_REQUIREMENTS" && err.details) {
        const fields: string[] = err.details.missing || [];
        setMissing({ stage: String(err.details.stage || stage), fields });
        toast({ title: tx("stageMissing", { fields: fields.map((k) => (k === "assignedTo" ? tx("assignTo") : fieldDef("lead", k)?.[lang] || k)).join(", ") }), variant: "destructive" });
      } else {
        toast({ title: tx("error"), description: err.message, variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (!id) return;
    if (dirty && !(await save({ quiet: true }))) return;
    setBusy(true);
    setMissing(null);
    try {
      const res = await crmFetch<CrmAcceptResult>("POST", `/api/crm/leads/${id}/accept`);
      applyServerRecord(res.lead);
      queryClient.invalidateQueries({ queryKey: crmKeys.list("contact") });
      toast({ title: res.contactCreated ? tx("acceptCreated") : tx("acceptLinked") });
    } catch (e) {
      const err = e as CrmError;
      if (err.code === "STAGE_REQUIREMENTS" && err.details) {
        setMissing({ stage: String(err.details.stage || "accept"), fields: err.details.missing || [] });
      }
      toast({ title: tx("error"), description: err.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const refresh = () => guard(async () => {
    const res = await rec.refetch();
    if (res.data) {
      const d = toDraft(entity, res.data.values);
      setInitial(d);
      setDraft(d);
    }
    queryClient.invalidateQueries({ queryKey: crmKeys.timeline(entity, id || "new") });
  });

  // ── Rendering helpers ────────────────────────────────────────────────────
  const requiredKeys = useMemo(() => {
    const s = new Set<string>();
    for (const sec of layout?.sections || []) for (const f of sec.fields) if (f.required) s.add(f.key);
    if (entity === "lead") { s.add("topic"); s.add("contactId"); }
    return s;
  }, [layout, entity]);
  const noEmail = entity === "contact" && !String(draft.personalEmail || "").trim() && !String(draft.corporateEmail || "").trim();
  const bothEmails = entity === "contact" && !!String(draft.personalEmail || "").trim() && !!String(draft.corporateEmail || "").trim();

  /** Star on the label of each e-mail: which one is the default (used to send documents). */
  const defaultStar = (key: string) => {
    if (entity !== "contact" || (key !== "personalEmail" && key !== "corporateEmail")) return undefined;
    const kind = key === "personalEmail" ? "personal" : "corporate";
    const filled = !!String(draft[key] || "").trim();
    const isDefault = filled && (bothEmails ? (draft.preferredEmail || "personal") === kind : true);
    if (!filled || (!bothEmails && !isDefault)) return undefined;
    if (isDefault) {
      return (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400" title={tx("defaultEmailHint")} data-testid={`default-email-${key}`}>
          <Star className="h-3 w-3 fill-current" /> {tx("defaultEmail")}
        </span>
      );
    }
    if (readOnly) return undefined;
    return (
      <button type="button" onClick={() => setValue("preferredEmail", kind)} title={tx("defaultEmailHint")}
        className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`make-default-${key}`}>
        <Star className="h-3 w-3" /> {tx("makeDefault")}
      </button>
    );
  };

  /** A client picked for the lead: its person fields show on the lead right away (the API keeps them in step). */
  const applyClient = (r: CrmRecord) => {
    const v = r.values;
    setValues({
      contactId: r.id,
      firstName: v.firstName || "",
      lastName: v.lastName || "",
      jobTitle: v.jobTitle || "",
      email: v.email || "",
      mobilePhone: v.mobilePhone ? formatPhoneDisplay(v.mobilePhone) : "",
      businessPhone: v.businessPhone ? formatPhoneDisplay(v.businessPhone) : "",
      ...(String(draft.companyName || "").trim() || !v.companyName ? {} : { companyName: v.companyName }),
    });
    setErrors((e) => { const { contactId: _drop, ...rest } = e; return rest; });
    if (missing) setMissing(null);
  };

  const title = recordTitle(entity, draft) || (isNew ? (entity === "lead" ? tx("newLead") : tx("newContact")) : "—");
  const subtitle = entity === "lead"
    ? [entityLabel(entity, lang), draft.topic && recordTitle(entity, { ...draft, fullName: "" }) !== draft.topic ? draft.topic : "", draft.companyName].filter(Boolean).join(" · ")
    : [entityLabel(entity, lang), draft.jobTitle, draft.companyName].filter(Boolean).join(" · ");
  const ownerName = ctx.members.find((m) => m.userId === draft.ownerId)?.name || record?.names?.ownerId || "";
  const phoneDigits = onlyDigits(phoneToE164(draft.mobilePhone) || "");

  const renderSection = (section: CrmLayoutSection) => (
    <Card key={section.id} data-testid={`section-${section.id}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">{sectionLabel(section.id, section.label, lang, entity)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {section.fields.map((f) => {
            const def = fieldDef(entity, f.key);
            if (!def) return null;
            const wide = def.type === "textarea" || def.type === "lookup" || (entity === "lead" && f.key === "street1");
            // A lead's person fields are its client's: shown, edited on the client record.
            const mirrored = entity === "lead" && LEAD_MIRRORED.includes(f.key);
            return (
              <div key={f.key} className={wide ? "sm:col-span-2" : ""}>
                <CrmFieldInput
                  entity={entity}
                  def={def}
                  value={draft[f.key]}
                  onChange={(v) => setValue(f.key, v)}
                  onBlur={f.key === "postalCode" ? onPostalBlur : undefined}
                  error={errors[f.key]}
                  required={!mirrored && (requiredKeys.has(f.key)
                    || (entity === "contact" && f.key === "firstName" && !String(draft.lastName || "").trim())
                    || (noEmail && (f.key === "personalEmail" || f.key === "corporateEmail")))}
                  readOnly={readOnly || mirrored}
                  members={ctx.members}
                  userName={record?.names?.[f.key]}
                  labelAddon={defaultStar(f.key)}
                  onPickRecord={f.key === "contactId" ? applyClient : undefined}
                  lookupPrefill={f.key === "contactId" ? draft : undefined}
                />
                {duplicate?.id && f.key === duplicate.field && (
                  <Link href={`/contacts/${duplicate.id}`} className="mt-1 inline-block text-xs underline underline-offset-2">{tx("open")}: {duplicate.name}</Link>
                )}
              </div>
            );
          })}
        </div>
        {entity === "lead" && section.fields.some((f) => f.key === "postalCode") && (
          <LeadAddressPanel draft={draft} setValues={setValues} readOnly={readOnly} />
        )}
      </CardContent>
    </Card>
  );

  const dateTimePair = (label: string, iso: string | null, testId: string) => (
    <FieldShell label={label}>
      <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-2">
        <ReadOnlyBox value={iso ? formatDateTime(iso, lang, false) : ""} testId={`${testId}-date`} />
        <ReadOnlyBox value={iso ? formatTime(iso, lang) : ""} testId={`${testId}-time`} />
      </div>
    </FieldShell>
  );

  const systemSection = (
    <Card data-testid="section-system">
      <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">{sectionLabel("system", null, lang)}</CardTitle></CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {dateTimePair(fieldDef(entity, "createdOn")![lang], draft.createdOn, "sys-created")}
        <FieldShell label={fieldDef(entity, "createdBy")![lang]}><ReadOnlyBox value={record?.names?.createdBy || ""} testId="sys-created-by" /></FieldShell>
        {dateTimePair(fieldDef(entity, "modifiedOn")![lang], draft.modifiedOn, "sys-modified")}
        <FieldShell label={fieldDef(entity, "modifiedBy")![lang]}><ReadOnlyBox value={record?.names?.modifiedBy || ""} testId="sys-modified-by" /></FieldShell>
        {entity === "contact" && dateTimePair(fieldDef("contact", "lastDocumentAt")![lang], draft.lastDocumentAt, "sys-last-doc")}
        {entity === "lead" && draft.acceptedAt && dateTimePair(fieldDef("lead", "acceptedAt")![lang], draft.acceptedAt, "sys-accepted")}
      </CardContent>
    </Card>
  );

  if (!isNew && rec.error) {
    return (
      <Card>
        <CardContent className="space-y-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">{rec.error.status === 404 ? tx("notFound") : rec.error.message}</p>
          <Button variant="outline" onClick={() => navigate(route)}><ArrowLeft className="h-4 w-4" /> {tx("back")}</Button>
        </CardContent>
      </Card>
    );
  }
  if (!loaded || !layout) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {tx("loading")}
      </div>
    );
  }

  const cmd = "shrink-0";
  return (
    <div className="space-y-3" data-testid={`crm-record-${entity}`}>
      {confirmDialog}

      {/* Command bar */}
      <div className={`sticky ${sidebarPlacement === "left" ? "top-0" : "top-[65px]"} z-20 flex items-center gap-1 overflow-x-auto rounded-lg border bg-card/95 px-2 py-1.5 backdrop-blur`}
        data-testid="record-commands">
        <Button variant="ghost" size="icon" className={`${cmd} h-8 w-8`} title={tx("back")} onClick={() => guard(() => navigate(route))} data-testid="record-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className={cmd} onClick={() => save()} disabled={saving || readOnly || (!dirty && !isNew)} data-testid="record-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tx("save")}
        </Button>
        <Button variant="ghost" size="sm" className={cmd} onClick={() => save({ close: true })} disabled={saving || readOnly} data-testid="record-save-close">
          <SaveAll className="h-4 w-4" /> {tx("saveClose")}
        </Button>
        <Button variant="ghost" size="sm" className={cmd} onClick={() => guard(() => navigate(`${route}/new`))} data-testid="record-new">
          <Plus className="h-4 w-4" /> {tx("new")}
        </Button>
        {!isNew && (
          <>
            <div className="mx-1 h-5 w-px shrink-0 bg-border" />
            <Button variant="ghost" size="sm" className={cmd} onClick={sendDocument} disabled={busy} data-testid="record-send">
              <Send className="h-4 w-4" /> {tx("sendDocument")}
            </Button>
            {entity === "contact" ? (
              active ? (
                <Button variant="ghost" size="sm" className={cmd} onClick={() => setState(false)} disabled={busy} data-testid="record-deactivate">
                  <PowerOff className="h-4 w-4" /> {tx("deactivate")}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" className={cmd} onClick={() => setState(true)} disabled={busy} data-testid="record-activate">
                  <Power className="h-4 w-4" /> {tx("activate")}
                </Button>
              )
            ) : active ? (
              <Button variant="ghost" size="sm" className={cmd} onClick={disqualify} disabled={busy} data-testid="record-disqualify">
                <Ban className="h-4 w-4" /> {tx("disqualify")}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className={cmd} onClick={() => setState(true)} disabled={busy} data-testid="record-reopen">
                <RotateCcw className="h-4 w-4" /> {tx("reopen")}
              </Button>
            )}
            <Button variant="ghost" size="sm" className={cmd} onClick={remove} disabled={busy} data-testid="record-delete">
              <Trash2 className="h-4 w-4 text-destructive" /> {tx("delete")}
            </Button>
            <Button variant="ghost" size="sm" className={cmd} onClick={refresh} data-testid="record-refresh">
              <RefreshCw className={`h-4 w-4 ${rec.isFetching ? "animate-spin" : ""}`} /> {tx("refresh")}
            </Button>
          </>
        )}
        {form.data?.canCustomize && (
          <Button variant="ghost" size="sm" className={`${cmd} ml-auto`} onClick={() => setDesignerOpen(true)} data-testid="record-customize">
            <Settings2 className="h-4 w-4" /> <span className="hidden md:inline">{tx("customizeForm")}</span>
          </Button>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-card p-4">
        <div className="flex min-w-0 items-center gap-3">
          {entity === "contact" && !isNew
            ? <ContactAvatar record={record} name={title} editable={!readOnly} onChanged={applyServerRecord} />
            : <Initials name={title} />}
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 break-words text-xl font-bold sm:text-2xl" data-testid="record-title">
              {title}
              {dirty && <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground" data-testid="record-unsaved">{tx("unsaved")}</span>}
            </h1>
            <p className="truncate text-sm text-muted-foreground">{subtitle}</p>
            {!isNew && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {draft.email && (
                  <a href={`mailto:${draft.email}`} className="inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover-elevate" title={tx("writeEmail")}>
                    <Mail className="h-3.5 w-3.5" /> <span className="max-w-[14rem] truncate">{draft.email}</span>
                  </a>
                )}
                {phoneDigits && (
                  <>
                    <a href={`tel:+${phoneDigits}`} className="inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover-elevate" title={tx("callPhone")}>
                      <Phone className="h-3.5 w-3.5" /> {formatPhoneDisplay(draft.mobilePhone)}
                    </a>
                    <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer" className="inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover-elevate"
                      title={tx("whatsapp")}>
                      <MessageCircle className="h-3.5 w-3.5" /> {tx("whatsapp")}
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:flex sm:flex-wrap sm:items-start">
          {entity === "lead" && (
            <>
              <div><dt className="text-xs text-muted-foreground">{fieldDef("lead", "status")![lang]}</dt><dd className="font-medium" data-testid="header-status">{optionLabel("status", draft.status, lang) || "—"}</dd></div>
              <div><dt className="text-xs text-muted-foreground">{fieldDef("lead", "rating")![lang]}</dt><dd className="font-medium">{optionLabel("rating", draft.rating, lang) || "—"}</dd></div>
            </>
          )}
          <div><dt className="text-xs text-muted-foreground">{fieldDef(entity, "isActive")![lang]}</dt><dd className="font-medium" data-testid="header-state">{stateLabel(entity, active, lang)}</dd></div>
          {!isNew && <div><dt className="text-xs text-muted-foreground">{fieldDef(entity, "createdOn")![lang]}</dt><dd className="font-medium">{formatDateTime(draft.createdOn, lang, false)}</dd></div>}
          <div className="col-span-2 min-w-[12rem] sm:col-span-1">
            <dt className="text-xs text-muted-foreground">{fieldDef(entity, "ownerId")![lang]}</dt>
            <dd>
              {readOnly ? (
                <span className="font-medium">{ownerName || "—"}</span>
              ) : (
                <NativeSelect value={draft.ownerId || ""} onChange={(e) => setValue("ownerId", e.target.value)} className="mt-0.5 h-8" data-testid="header-owner">
                  {!ctx.members.some((m) => m.userId === draft.ownerId) && draft.ownerId && <option value={draft.ownerId}>{ownerName || draft.ownerId}</option>}
                  {ctx.members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </NativeSelect>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* Lead process */}
      {entity === "lead" && (
        <CrmBpf saved={!isNew} draft={draft} setValue={setValue} errors={errors} members={ctx.members} readOnly={readOnly} busy={busy || saving}
          missing={missing} onMove={moveStage} onAccept={accept} onPickClient={applyClient} />
      )}

      {/* Read-only banners */}
      {readOnly && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="record-readonly-banner">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span>
            {entity === "contact"
              ? tx("inactiveContact")
              : draft.status === "accepted"
                ? tx("acceptedLead", { date: formatDateTime(draft.acceptedAt, lang) })
                : draft.status === "not_qualified" ? tx("disqualifiedLead") : tx("closedLead")}
          </span>
          {entity === "lead" && draft.contactId && (
            <Link href={`/contacts/${draft.contactId}`} className="font-medium underline underline-offset-2" data-testid="banner-open-contact">{tx("openContact")}</Link>
          )}
        </div>
      )}

      {/* Leads from before every lead had a client: link one to keep editing. */}
      {entity === "lead" && !isNew && !readOnly && !draft.contactId && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="lead-needs-client">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span>{tx("leadNeedsClient")}</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="summary" data-testid="tab-summary">{tx("summary")}</TabsTrigger>
          <TabsTrigger value="files" disabled={isNew} data-testid="tab-files">{tx("files")}</TabsTrigger>
          <TabsTrigger value="audit" disabled={isNew} data-testid="tab-audit">{tx("auditHistory")}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-3">
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <div className="space-y-4">
              {layout.sections.map(renderSection)}
              {systemSection}
            </div>
            <div>
              <CrmTimeline entity={entity} recordId={id} readOnly={readOnly} recordEmail={draft.email || undefined} onSendDocument={sendDocument} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="files" className="mt-3">
          {id && <CrmFiles entity={entity} recordId={id} onSendDocument={sendDocument} canSend={!readOnly} />}
        </TabsContent>
        <TabsContent value="audit" className="mt-3">
          {id && <CrmAudit entity={entity} recordId={id} members={ctx.members} />}
        </TabsContent>
      </Tabs>

      {form.data && (
        <CrmFormDesigner
          entity={entity}
          open={designerOpen}
          onOpenChange={setDesignerOpen}
          layout={form.data.layout}
          isCustom={form.data.isCustom}
          onSaved={(f) => queryClient.setQueryData(crmKeys.form(entity), { ...f, canCustomize: true })}
        />
      )}
    </div>
  );
}
